/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError, CreateOauthClientRequest, CreateOauthClientResponse2 } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type CreateOAuthClientMutationConfig = {
  onSuccess?: (data: CreateOauthClientResponse2, variables: CreateOauthClientRequest) => void;
  onError?: (error: unknown, variables: CreateOauthClientRequest) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
};

/**
 * Hook for creating a new OAuth client through the management plane API.
 *
 * @param config.onSuccess - Called after successful OAuth client creation
 * @param config.onError - Called if creation fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @returns React Query mutation result for creating OAuth clients
 *
 * @example
 * ```tsx
 * const { mutate } = useCreateOAuthClientMutation({
 *   onSuccess: (data) => console.log('Client created:', data.clientSecret),
 *   onError: (error) => console.error('Creation failed:', error)
 * });
 *
 * // Create OAuth client
 * mutate({
 *   name: 'My App',
 *   redirectUris: ['https://example.com/callback'],
 *   idTokenSignatureAlg: 'RS256'
 * });
 * ```
 */
export function useCreateOAuthClientMutation(config: CreateOAuthClientMutationConfig) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const queryClient = useQueryClient();

  const { mutate, isError, error, isPending } = useMutation<
    CreateOauthClientResponse2,
    ApiError,
    CreateOauthClientRequest
  >({
    mutationFn: (client: CreateOauthClientRequest) => {
      return mgmtPlaneAPIClient.oauthClients.createOauthClient({
        tenantId: tenantId!,
        requestBody: {
          name: client.name,
          redirectUris: client.redirectUris,
          idTokenSignatureAlg: client.idTokenSignatureAlg,
        },
      });
    },
    onSuccess: (data, variables) => {
      // Manually invalidate the OAuth clients query cache
      queryClient.invalidateQueries({ queryKey: ['oauthClients'] });

      // Call the user-provided onSuccess callback
      config?.onSuccess?.(data, variables);
    },
    onError: config?.onError,
    onSettled: config?.onSettled,
  });

  return { mutate, isError, error, isPending };
}
